import moment from 'moment';
import 'moment-timezone';
import { TIMEZONE } from 'src/constants/common';

/**
 * Whose clock the portal shows (Lucas, 2026-09-05): moments in time (location pings, status
 * changes, "today", "25 minutes ago", Radar timestamps) are on the READER's clock; appointments
 * at a place (pickup and delivery windows, dock ETAs) stay on the dock's clock from WALHQ.
 *
 * This module resolves the reader's clock. Order: the portal user's stored timezone (their
 * profile, or the browser timezone written back at login), then the browser, then the
 * organisation's appearance timezone (WAL's own clock, which every screen used to show), then UTC.
 * No redux import here so utilities and slices can both use it.
 */
export const isValidTimezone = (name: unknown): name is string => typeof name === 'string' && name.length > 0 && !!moment.tz.zone(name);

export const getBrowserTimezone = (): string | null => {
    try {
        const name = Intl.DateTimeFormat().resolvedOptions().timeZone;

        return isValidTimezone(name) ? name : null;
    } catch {
        return null;
    }
};

export interface ViewerTimezoneSources {
    /** users.timezone from the portal user payload */
    userTimezone?: string | null;
    /** appearance.timezone, the organisation's clock */
    orgTimezone?: string | null;
}

export const resolveViewerTimezone = (sources: ViewerTimezoneSources = {}): string => {
    if (isValidTimezone(sources.userTimezone)) return sources.userTimezone;

    const browser = getBrowserTimezone();
    if (browser) return browser;

    if (isValidTimezone(sources.orgTimezone)) return sources.orgTimezone;
    if (isValidTimezone(TIMEZONE)) return TIMEZONE;

    return 'UTC';
};

// Last resolved value, set by the auth slice once the user payload is known, so plain
// utilities (no hooks, no store) read the same clock as the components.
let resolvedViewerTimezone: string | null = null;

export const setResolvedViewerTimezone = (timezone: string): void => {
    resolvedViewerTimezone = isValidTimezone(timezone) ? timezone : resolvedViewerTimezone;
};

export const getViewerTimezone = (): string => resolvedViewerTimezone ?? resolveViewerTimezone();
